import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, AlertTriangle, Clock, User, Phone, Mail, CalendarDays } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AvailabilityService } from "@/services/availabilityService";
import { BookingService } from "@/services/bookingService";
import { AvailabilityPeriod } from "@/integrations/mysql/types";
import { useAuthState } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { buildPropertyWhatsAppMessage, buildWhatsAppUrl } from "@/lib/whatsapp";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (bookingData: BookingData) => void;
  propertyTitle: string;
  propertyId: number;
  propertyDescription?: string | null;
  propertyMaxGuests?: number;
  ownerName?: string;
  ownerPhone?: string;
  ownerPhoneDdi?: string;
  ownerEmail?: string;
  /** Exibe telefone/e-mail do proprietário (plano Live ou admin). */
  showOwnerContact?: boolean;
  loading?: boolean;
}

export interface BookingData {
  availabilityPeriodId: number;
  checkIn: string;
  checkOut: string;
  totalNights: number;
  totalPrice: number;
  guestNotes?: string;
}

const BookingModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  propertyTitle, 
  propertyId,
  propertyDescription,
  propertyMaxGuests,
  ownerName = "Proprietário",
  ownerPhone = "",
  ownerPhoneDdi = "55",
  ownerEmail = "",
  showOwnerContact = false,
  loading = false,
}: BookingModalProps) => {
  const [availabilityPeriods, setAvailabilityPeriods] = useState<AvailabilityPeriod[]>([]);
  const [userPendingPeriods, setUserPendingPeriods] = useState<AvailabilityPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<AvailabilityPeriod | null>(null);
  const [guestNotes, setGuestNotes] = useState("");
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const { toast } = useToast();
  
  const { userId } = useAuthState();

  const loadAvailabilityPeriods = useCallback(async () => {
    try {
      setLoadingPeriods(true);
      if (userId) {
        console.log('🔍 BookingModal: Carregando períodos para usuário:', { propertyId, userId, userIdType: typeof userId });
        // Carregar períodos disponíveis (excluindo os com solicitações pendentes do usuário)
        const [availablePeriods, pendingPeriods] = await Promise.all([
          AvailabilityService.getAvailabilityPeriodsForUser(propertyId, userId),
          AvailabilityService.getUserPendingPeriods(propertyId, userId)
        ]);
        console.log('📋 BookingModal: Períodos disponíveis encontrados:', availablePeriods.length);
        console.log('📋 BookingModal: Períodos pendentes encontrados:', pendingPeriods.length);
        console.log('📋 BookingModal: IDs dos períodos pendentes:', pendingPeriods.map(p => p.id));
        
        // Filtrar períodos disponíveis para EXCLUIR aqueles que já têm reserva pendente do usuário
        const pendingPeriodIds = new Set(pendingPeriods.map(p => p.id));
        const filteredAvailablePeriods = availablePeriods.filter(period => !pendingPeriodIds.has(period.id));
        
        console.log('📋 BookingModal: Períodos disponíveis após filtrar pendentes:', filteredAvailablePeriods.length);
        setAvailabilityPeriods(filteredAvailablePeriods);
        setUserPendingPeriods(pendingPeriods);
      } else {
        // Fallback para método padrão se não houver userId
        const periods = await AvailabilityService.getAvailabilityPeriods(propertyId);
        setAvailabilityPeriods(periods);
        setUserPendingPeriods([]);
      }
    } catch (error) {
      console.error('Erro ao carregar períodos de disponibilidade:', error);
    } finally {
      setLoadingPeriods(false);
    }
  }, [propertyId, userId]);

  // Carregar períodos de disponibilidade quando o modal abrir
  useEffect(() => {
    if (isOpen && propertyId) {
      loadAvailabilityPeriods();
    }
  }, [isOpen, propertyId, userId, loadAvailabilityPeriods]);


  const handlePeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    const period = availabilityPeriods.find(p => p.id.toString() === periodId);
    setSelectedPeriod(period || null);
  };

  // Calcular noites SEM timezone - usar datas exatas do banco
  const totalNights = () => {
    if (!selectedPeriod) return 0;
    
    // Extrair apenas a parte da data (YYYY-MM-DD)
    const startStr = typeof selectedPeriod.start_date === 'string' 
      ? selectedPeriod.start_date.substring(0, 10)
      : `${selectedPeriod.start_date.getUTCFullYear()}-${String(selectedPeriod.start_date.getUTCMonth() + 1).padStart(2, '0')}-${String(selectedPeriod.start_date.getUTCDate()).padStart(2, '0')}`;
    const endStr = typeof selectedPeriod.end_date === 'string'
      ? selectedPeriod.end_date.substring(0, 10)
      : `${selectedPeriod.end_date.getUTCFullYear()}-${String(selectedPeriod.end_date.getUTCMonth() + 1).padStart(2, '0')}-${String(selectedPeriod.end_date.getUTCDate()).padStart(2, '0')}`;
    
    // Parsear diretamente como YYYY-MM-DD usando UTC para evitar problemas de timezone
    const parseDate = (dateStr: string) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day));
      }
      return new Date(dateStr);
    };
    
    const start = parseDate(startStr);
    const end = parseDate(endStr);
    const timeDiff = end.getTime() - start.getTime();
    const nights = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    
    return nights > 0 ? nights : 0;
  };

  const calculateTotal = () => {
    if (!selectedPeriod) return 0;
    const nights = totalNights();
    return nights * selectedPeriod.price_per_night;
  };

  // Formatar data SEM timezone - usar exatamente como vem do banco (YYYY-MM-DD)
  // IMPORTANTE: NÃO usar toLocaleDateString ou qualquer função que aplique timezone
  const formatDate = (date: Date | string) => {
    let dateStr: string;
    
    // Extrair apenas a parte da data (YYYY-MM-DD)
    if (typeof date === 'string') {
      dateStr = date.substring(0, 10);
    } else {
      // Se é Date, extrair componentes UTC para evitar timezone
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Formatar diretamente da string YYYY-MM-DD sem criar Date object
    // Isso evita qualquer conversão de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      // Retornar no formato DD/MM/YYYY diretamente da string
      return `${day}/${month}/${year}`;
    }
    
    return dateStr;
  };

  const handleConfirm = async () => {
    if (!selectedPeriod || !userId) return;

    // Verificação adicional: garantir que o período não está na lista de pendentes
    const isPeriodPending = userPendingPeriods.some(period => period.id === selectedPeriod.id);
    if (isPeriodPending) {
      console.log('Período já possui solicitação pendente do usuário');
      return;
    }

    // Verificar se o usuário já tem uma reserva pendente ou confirmada para este período EXATO
    try {
      console.log('🔍 Verificando se período já tem reserva pendente:', {
        periodId: selectedPeriod.id,
        startDate: selectedPeriod.start_date,
        endDate: selectedPeriod.end_date,
        userId: userId
      });
      
      const hasPendingBooking = await BookingService.hasPendingBookingForPeriod(userId, selectedPeriod.id);
      console.log('📋 Resultado da verificação:', hasPendingBooking);
      
      if (hasPendingBooking) {
        // Não permitir criar nova reserva se já existe uma pendente ou confirmada para o período EXATO
        console.log('⚠️ Usuário já tem uma reserva pendente ou confirmada para este período EXATO:', selectedPeriod.id);
        toast({
          title: "Reserva já existe",
          description: `Você já possui uma solicitação pendente ou confirmada para o período de ${formatDate(selectedPeriod.start_date)} a ${formatDate(selectedPeriod.end_date)}. Não é possível criar uma nova enquanto a atual estiver pendente ou confirmada.`,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar reservas pendentes:', error);
      // Continuar mesmo com erro na verificação, mas a verificação no backend vai pegar
    }

    // Usar as datas EXATAS do período de disponibilidade (sem nenhuma conversão de timezone)
    const getDateString = (date: Date | string): string => {
      // Se já é uma string no formato YYYY-MM-DD, retornar diretamente (como vem do banco)
      if (typeof date === 'string') {
        // Se já está no formato correto, usar diretamente
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        // Se for uma string ISO (com hora), extrair apenas a parte da data
        if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
          return date.substring(0, 10);
        }
      }
      
      // Se for um objeto Date, extrair apenas os componentes da data LOCAL (sem timezone)
      // IMPORTANTE: Usar getFullYear(), getMonth(), getDate() que retornam valores locais
      // NÃO usar toLocaleDateString ou outras funções que aplicam timezone
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Usar UTC para evitar problemas de timezone
      // Mas esperar: o banco retorna DATE (não DATETIME), então deve vir como string
      // Se chegou aqui como Date, pode ter sido convertido incorretamente
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    console.log('📅 Período selecionado (RAW):', {
      id: selectedPeriod.id,
      start_date: selectedPeriod.start_date,
      end_date: selectedPeriod.end_date,
      start_date_type: typeof selectedPeriod.start_date,
      start_date_value: selectedPeriod.start_date,
      end_date_value: selectedPeriod.end_date
    });

    // Usar as datas EXATAS do período, sem nenhuma alteração
    const checkIn = getDateString(selectedPeriod.start_date);
    const checkOut = getDateString(selectedPeriod.end_date);
    
    console.log('📅 Datas finais para reserva (devem ser EXATAS do período):', { 
      checkIn, 
      checkOut,
      periodo_start: selectedPeriod.start_date,
      periodo_end: selectedPeriod.end_date
    });

    const bookingData: BookingData = {
      availabilityPeriodId: selectedPeriod.id,
      checkIn: checkIn,
      checkOut: checkOut,
      totalNights: totalNights(),
      totalPrice: calculateTotal(),
      guestNotes: guestNotes.trim() || undefined,
    };

    onConfirm(bookingData);
  };

  const isFormValid = selectedPeriodId && selectedPeriod && !userPendingPeriods.some(period => period.id === selectedPeriod.id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Solicitar Reserva
          </DialogTitle>
          <DialogDescription>
            Confirme os detalhes da sua solicitação de reserva para{" "}
            <span className="font-semibold">{propertyTitle}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto flex-1 pr-2">
          {/* Informações da Propriedade */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Detalhes da Propriedade</h3>
            <p className="text-sm text-muted-foreground mb-2">{propertyTitle}</p>
          </div>

          {/* Seleção de Período */}
          <div className="space-y-4">
            <Label htmlFor="period">Período de Disponibilidade</Label>
            {loadingPeriods ? (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Carregando períodos...</span>
              </div>
            ) : availabilityPeriods.length === 0 && userPendingPeriods.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <CalendarDays className="h-8 w-8 mx-auto mb-2" />
                <p>Nenhum período disponível encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Períodos Disponíveis */}
                {availabilityPeriods.length > 0 && (
                  <div>
                    <Select value={selectedPeriodId} onValueChange={handlePeriodChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um período disponível">
                          {selectedPeriod ? (
                            `${formatDate(selectedPeriod.start_date)} - ${formatDate(selectedPeriod.end_date)}`
                          ) : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {availabilityPeriods.map((period) => (
                          <SelectItem 
                            key={period.id} 
                            value={period.id.toString()}
                          >
                            <div className="flex flex-col w-full">
                              <div className="flex items-center justify-between w-full gap-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-foreground group-hover:text-white">
                                    {formatDate(period.start_date)} - {formatDate(period.end_date)}
                                  </span>
                                  {period.availability_type && (
                                    <Badge variant="outline" className="text-xs">
                                      {period.availability_type === 'troca' ? 'Troca' : 
                                       period.availability_type === 'aluguel' ? 'Aluguel' : 
                                       'Troca/Aluguel'}
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-sm font-semibold text-foreground group-hover:text-white">
                                  R$ {period.price_per_night.toLocaleString('pt-BR')}/noite
                                </span>
                              </div>
                              <div className="flex items-center justify-between w-full text-xs gap-4">
                                <span className="text-foreground group-hover:text-white">
                                  {(period.max_guests && period.max_guests > 0) ? period.max_guests : (propertyMaxGuests || 0)} hóspede{((period.max_guests && period.max_guests > 0) ? period.max_guests : (propertyMaxGuests || 0)) !== 1 ? 's' : ''}
                                </span>
                                {period.notes && (
                                  <span className="truncate max-w-[200px] text-foreground group-hover:text-white" title={period.notes}>
                                    {period.notes}
                                  </span>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedPeriod && (
                      <div className="mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                        <div className="flex flex-col gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            <span className="font-medium">Período:</span>
                            <span>{formatDate(selectedPeriod.start_date)} - {formatDate(selectedPeriod.end_date)}</span>
                            {selectedPeriod.availability_type && (
                              <Badge variant="outline" className="text-xs ml-2">
                                {selectedPeriod.availability_type === 'troca' ? 'Troca' : 
                                 selectedPeriod.availability_type === 'aluguel' ? 'Aluguel' : 
                                 'Troca/Aluguel'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-primary" />
                            <span className="font-medium">Hóspedes:</span>
                            <span className="font-semibold text-primary">
                              {(selectedPeriod.max_guests && selectedPeriod.max_guests > 0) ? selectedPeriod.max_guests : (propertyMaxGuests || 0)} 
                              {((selectedPeriod.max_guests && selectedPeriod.max_guests > 0) ? selectedPeriod.max_guests : (propertyMaxGuests || 0)) === 1 ? ' hóspede' : ' hóspedes'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Períodos com Solicitações Pendentes */}
                {userPendingPeriods.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">Suas Solicitações Pendentes</span>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                      {userPendingPeriods.map((period) => (
                        <div key={period.id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100">
                          <div className="flex items-center gap-3">
                            <Calendar className="h-4 w-4 text-blue-600" />
                            <div>
                              <span className="text-sm font-medium text-blue-800">
                                {formatDate(period.start_date)} - {formatDate(period.end_date)}
                              </span>
                              <div className="text-xs text-blue-600">
                                {(period.max_guests && period.max_guests > 0) ? period.max_guests : (propertyMaxGuests || 0)} hóspedes • R$ {period.price_per_night.toLocaleString('pt-BR')}/noite
                              </div>
                            </div>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            Aguardando Resposta
                          </span>
                        </div>
                      ))}
                    </div>
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertTriangle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <div className="space-y-1">
                          <p className="font-semibold text-sm">Solicitação Pendente</p>
                          <p className="text-sm">
                            Você já possui {userPendingPeriods.length} solicitaç{userPendingPeriods.length > 1 ? 'ões' : 'ão'} pendente{userPendingPeriods.length > 1 ? 's' : ''} para este imóvel. 
                            Os períodos com solicitações pendentes não aparecem na lista de opções disponíveis.
                            Aguarde a resposta do proprietário antes de fazer novas solicitações.
                          </p>
                        </div>
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resumo da Reserva */}
          {isFormValid && selectedPeriod && (
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
              <h3 className="font-semibold mb-3">Resumo da Solicitação</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span>Período:</span>
                  <span className="text-right">{formatDate(selectedPeriod.start_date)} - {formatDate(selectedPeriod.end_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Duração:</span>
                  <span>{totalNights()} noite{totalNights() !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span>Valor/noite:</span>
                  <span>R$ {selectedPeriod.price_per_night.toLocaleString('pt-BR')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Hóspedes:</span>
                  <span>{(selectedPeriod.max_guests && selectedPeriod.max_guests > 0) ? selectedPeriod.max_guests : (propertyMaxGuests || 0)}</span>
                </div>
              </div>
              <div className="flex justify-between font-semibold text-lg border-t pt-2 mt-2">
                <span>Total:</span>
                <span className="text-primary">
                  R$ {calculateTotal().toLocaleString('pt-BR')}
                </span>
              </div>
            </div>
          )}

          {/* Observações */}
          <div className="space-y-2">
            <Label htmlFor="guestNotes">Observações (opcional)</Label>
            <Textarea
              id="guestNotes"
              placeholder="Informações adicionais sobre sua estadia..."
              value={guestNotes}
              onChange={(e) => setGuestNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Avisos Importantes */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-3">
                <p className="font-semibold text-sm">Informações Importantes:</p>
                <p className="text-xs leading-relaxed">
                  Agradecemos sua solicitação de reserva! Para que todo o processo seja claro e transparente, por favor, atente-se às seguintes informações:
                </p>
                <div className="space-y-2.5 text-xs">
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-foreground">• Confirmação:</span>
                    <span className="text-muted-foreground ml-4 leading-relaxed">
                      Sua solicitação é o primeiro passo e está sujeita à aprovação do proprietário, não garantindo a confirmação imediata da reserva.
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-foreground">• Prazo de Resposta:</span>
                    <span className="text-muted-foreground ml-4 leading-relaxed">
                      O proprietário terá um prazo de até 24 horas úteis para analisar sua solicitação e entrar em contato diretamente para aceitar ou rejeitar.
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-foreground">• Valores e Pagamento:</span>
                    <span className="text-muted-foreground ml-4 leading-relaxed">
                      Os detalhes sobre os valores finais e as formas de pagamento serão combinados diretamente entre você e o proprietário.
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-foreground">• Política de Uso:</span>
                    <span className="text-muted-foreground ml-4 leading-relaxed">
                      Para entender em detalhes todo o processo e as diretrizes da plataforma, recomendamos a leitura da nossa <a href="/politica" className="text-primary underline hover:text-primary/80 font-medium">Política de Uso</a>.
                    </span>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {showOwnerContact && (ownerPhone || ownerEmail) && (
            <Alert>
              <User className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-semibold text-sm">Contato do Proprietário:</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    {ownerPhone && (
                      <a
                        href={buildWhatsAppUrl(
                          ownerPhone,
                          ownerPhoneDdi,
                          buildPropertyWhatsAppMessage(
                            propertyTitle,
                            propertyDescription,
                          ),
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                        aria-label={`Falar com ${ownerName} no WhatsApp`}
                      >
                        <Phone className="h-3 w-3" />
                        <span>{ownerPhone}</span>
                      </a>
                    )}
                    {ownerEmail && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span>{ownerEmail}</span>
                      </div>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!isFormValid || loading}
            className="gap-2"
          >
            {loading ? (
              <>
                <Clock className="h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              "Confirmar Solicitação"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
